import React, { useCallback, useEffect, useState } from "react";
import employeesApi from "../../api/employeesApi";
import questionnaireApplicationsApi from "../../api/questionnaireApplicationsApi";
import styles from "./BranchConfigurationPage.module.css";

const PAGE_SIZE = 10;
const NOM_TYPES = new Set(["nom035", "nom035SecI"]);

function applicationIdentifier(application) {
  return String(
    application.questionnaireApplicationId?._id ||
    application.questionnaireApplicationId ||
    application._id
  );
}

export default function ApplicationEditModal({ application, onClose, onSaved }) {
  const applicationId = applicationIdentifier(application);
  const typeApplication = application.typeApplication;
  const isNom = NOM_TYPES.has(typeApplication);
  const answeredField = typeApplication === "nom035SecI"
    ? "answeredNom035SecI"
    : "answeredNom";

  const [applicationName, setApplicationName] = useState(application.applicationName || "");
  const [savingName, setSavingName] = useState(false);
  const [message, setMessage] = useState("");

  const [areas, setAreas] = useState([]);
  const [areasTotal, setAreasTotal] = useState(0);
  const [areasPage, setAreasPage] = useState(1);
  const [areasLoading, setAreasLoading] = useState(isNom);
  const [newArea, setNewArea] = useState("");
  const [editingAreaId, setEditingAreaId] = useState(null);
  const [editingArea, setEditingArea] = useState("");

  const [employees, setEmployees] = useState([]);
  const [employeesTotal, setEmployeesTotal] = useState(0);
  const [employeesPage, setEmployeesPage] = useState(1);
  const [employeesLoading, setEmployeesLoading] = useState(isNom);
  const [newEmail, setNewEmail] = useState("");
  const [newEmployeeNumber, setNewEmployeeNumber] = useState("");
  const [newEmployeeCode, setNewEmployeeCode] = useState("");
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [editingEmail, setEditingEmail] = useState("");
  const [editingEmployeeNumber, setEditingEmployeeNumber] = useState("");
  const [editingEmployeeCode, setEditingEmployeeCode] = useState("");
  const [usesEmployeeCredentials, setUsesEmployeeCredentials] = useState(false);
  const [applicationLoaded, setApplicationLoaded] = useState(false);
  const [savingList, setSavingList] = useState(false);

  const loadAreas = useCallback(async () => {
    if (!isNom) return;
    setAreasLoading(true);
    try {
      const result = await questionnaireApplicationsApi.getJobAreas(applicationId, {
        page: areasPage,
        pageSize: PAGE_SIZE,
      });
      setAreas(result.data || []);
      setAreasTotal(result.total || 0);
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudieron cargar las áreas.");
    } finally {
      setAreasLoading(false);
    }
  }, [applicationId, areasPage, isNom]);

  const loadEmployees = useCallback(async () => {
    if (!isNom || !applicationLoaded) return;
    setEmployeesLoading(true);
    try {
      const result = await employeesApi.getNomApplicationEmployees(applicationId, {
        page: employeesPage,
        pageSize: PAGE_SIZE,
      });
      setEmployees(result.data || []);
      setEmployeesTotal(result.total || 0);
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudieron cargar los empleados.");
    } finally {
      setEmployeesLoading(false);
    }
  }, [applicationId, applicationLoaded, employeesPage, isNom]);

  useEffect(() => {
    questionnaireApplicationsApi.getById(applicationId)
      .then((data) => {
        setApplicationName(data.applicationName || "");
        setUsesEmployeeCredentials(Boolean(data.usesEmployeeCredentials));
        setApplicationLoaded(true);
      })
      .catch((error) => {
        setEmployeesLoading(false);
        setMessage(error.response?.data?.message || "No se pudo cargar la aplicación.");
      });
  }, [applicationId]);

  useEffect(() => { loadAreas(); }, [loadAreas]);
  useEffect(() => { loadEmployees(); }, [loadEmployees]);

  const saveName = async (event) => {
    event.preventDefault();
    const name = applicationName.trim();
    if (!name) return;
    setSavingName(true);
    setMessage("");
    try {
      await questionnaireApplicationsApi.update(applicationId, name);
      setApplicationName(name);
      setMessage("Nombre actualizado correctamente.");
      await onSaved();
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo actualizar el nombre.");
    } finally {
      setSavingName(false);
    }
  };

  const addArea = async (event) => {
    event.preventDefault();
    const value = newArea.trim();
    if (!value) return;
    setSavingList(true);
    setMessage("");
    try {
      await questionnaireApplicationsApi.createJobArea(applicationId, value);
      setNewArea("");
      setMessage("Área agregada correctamente.");
      if (areasPage !== 1) setAreasPage(1);
      else await loadAreas();
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo agregar el área.");
    } finally {
      setSavingList(false);
    }
  };

  const saveArea = async (areaId) => {
    const value = editingArea.trim();
    if (!value) return;
    setSavingList(true);
    setMessage("");
    try {
      await questionnaireApplicationsApi.updateJobArea(applicationId, areaId, value);
      setEditingAreaId(null);
      setEditingArea("");
      setMessage("Área actualizada correctamente.");
      await loadAreas();
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo actualizar el área.");
    } finally {
      setSavingList(false);
    }
  };

  const deleteArea = async (area) => {
    if (!window.confirm(`¿Eliminar el área ${area.jobArea}?`)) return;
    setSavingList(true);
    setMessage("");
    try {
      await questionnaireApplicationsApi.deleteJobArea(applicationId, area._id);
      setMessage("Área eliminada correctamente.");
      if (areas.length === 1 && areasPage > 1) setAreasPage((page) => page - 1);
      else await loadAreas();
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo eliminar el área.");
    } finally {
      setSavingList(false);
    }
  };

  const addEmployee = async (event) => {
    event.preventDefault();
    const email = newEmail.trim().toLowerCase();
    const employee_number = newEmployeeNumber.trim();
    const employee_code = newEmployeeCode.trim();
    if (usesEmployeeCredentials) {
      if (!employee_number || !employee_code) return;
    } else if (!email) return;
    setSavingList(true);
    setMessage("");
    try {
      await employeesApi.createNomApplicationEmployee(
        applicationId,
        usesEmployeeCredentials ? { employee_number, employee_code } : email
      );
      setNewEmail("");
      setNewEmployeeNumber("");
      setNewEmployeeCode("");
      setMessage("Empleado agregado correctamente.");
      if (employeesPage !== 1) setEmployeesPage(1);
      else await loadEmployees();
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo agregar el empleado.");
    } finally {
      setSavingList(false);
    }
  };

  const saveEmployee = async (employeeId) => {
    const email = editingEmail.trim().toLowerCase();
    const employee_number = editingEmployeeNumber.trim();
    const employee_code = editingEmployeeCode.trim();
    if (usesEmployeeCredentials) {
      if (!employee_number || !employee_code) return;
    } else if (!email) return;
    setSavingList(true);
    setMessage("");
    try {
      await employeesApi.updateNomApplicationEmployee(
        applicationId,
        employeeId,
        usesEmployeeCredentials ? { employee_number, employee_code } : email
      );
      setEditingEmployeeId(null);
      setEditingEmail("");
      setEditingEmployeeNumber("");
      setEditingEmployeeCode("");
      setMessage("Empleado actualizado correctamente.");
      await loadEmployees();
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo actualizar el empleado.");
    } finally {
      setSavingList(false);
    }
  };

  const deleteEmployee = async (employee) => {
    const employeeLabel = usesEmployeeCredentials
      ? `${employee.employee_number} / ${employee.employee_code}`
      : employee.email;
    if (!window.confirm(`¿Eliminar a ${employeeLabel}?`)) return;
    setSavingList(true);
    setMessage("");
    try {
      await employeesApi.deleteNomApplicationEmployee(applicationId, employee._id);
      setMessage("Empleado eliminado correctamente.");
      if (employees.length === 1 && employeesPage > 1) {
        setEmployeesPage((page) => page - 1);
      } else {
        await loadEmployees();
      }
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo eliminar el empleado.");
    } finally {
      setSavingList(false);
    }
  };

  const areasPages = Math.max(Math.ceil(areasTotal / PAGE_SIZE), 1);
  const employeesPages = Math.max(Math.ceil(employeesTotal / PAGE_SIZE), 1);

  return (
    <div className={styles.modalOverlay} role="presentation" onMouseDown={onClose}>
      <section
        className={`${styles.employeeModal} ${styles.applicationModal}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="application-edit-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.employeeModalHeader}>
          <div>
            <span>Configuración de cuestionario</span>
            <h2 id="application-edit-title">Editar aplicación</h2>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Cerrar">×</button>
        </div>

        <form className={styles.applicationNameForm} onSubmit={saveName}>
          <label htmlFor="edit-application-name">Nombre de la aplicación</label>
          <div className={styles.inlineRow}>
            <input
              id="edit-application-name"
              className={styles.input}
              value={applicationName}
              onChange={(event) => setApplicationName(event.target.value)}
            />
            <button type="submit" className={styles.primaryButton} disabled={savingName || !applicationName.trim()}>
              {savingName ? "Guardando..." : "Guardar nombre"}
            </button>
          </div>
        </form>

        {message && <div className={styles.message}>{message}</div>}

        {isNom && (
          <div className={styles.applicationDataGrid}>
            <section className={styles.applicationDataSection}>
              <div className={styles.listSectionHeader}>
                <div>
                  <h3>Áreas de trabajo</h3>
                  <p>{areasTotal} registradas</p>
                </div>
              </div>
              <form className={styles.listAddForm} onSubmit={addArea}>
                <input
                  className={styles.input}
                  placeholder="Nueva área de trabajo"
                  value={newArea}
                  onChange={(event) => setNewArea(event.target.value)}
                />
                <button type="submit" className={styles.primaryButton} disabled={savingList || !newArea.trim()}>
                  Agregar
                </button>
              </form>
              <div className={styles.editableList}>
                {areasLoading ? <p>Cargando áreas...</p> : areas.length ? areas.map((area) => (
                  <div className={styles.editableListItem} key={area._id}>
                    {editingAreaId === area._id ? (
                      <input className={styles.input} value={editingArea} onChange={(event) => setEditingArea(event.target.value)} />
                    ) : <span>{area.jobArea}</span>}
                    <div className={styles.employeeActions}>
                      {editingAreaId === area._id ? (
                        <>
                          <button type="button" disabled={savingList} onClick={() => saveArea(area._id)}>Guardar</button>
                          <button type="button" onClick={() => setEditingAreaId(null)}>Cancelar</button>
                        </>
                      ) : (
                        <>
                          <button type="button" onClick={() => { setEditingAreaId(area._id); setEditingArea(area.jobArea); }}>Editar</button>
                          <button type="button" className={styles.dangerButton} disabled={savingList} onClick={() => deleteArea(area)}>Eliminar</button>
                        </>
                      )}
                    </div>
                  </div>
                )) : <p>No hay áreas registradas.</p>}
              </div>
              <div className={styles.employeePagination}>
                <span>Página {areasPage} de {areasPages}</span>
                <div>
                  <button type="button" disabled={areasPage <= 1 || areasLoading} onClick={() => setAreasPage((page) => page - 1)}>Anterior</button>
                  <button type="button" disabled={areasPage >= areasPages || areasLoading} onClick={() => setAreasPage((page) => page + 1)}>Siguiente</button>
                </div>
              </div>
            </section>

            <section className={styles.applicationDataSection}>
              <div className={styles.listSectionHeader}>
                <div>
                  <h3>Empleados</h3>
                  <p>{employeesTotal} registrados</p>
                  <p>
                    Identificación por {usesEmployeeCredentials
                      ? "número y código de empleado"
                      : "correo electrónico"}.
                  </p>
                </div>
              </div>
              <form className={styles.listAddForm} onSubmit={addEmployee}>
                {usesEmployeeCredentials ? (
                  <>
                    <input
                      className={styles.input}
                      placeholder="Número de empleado"
                      value={newEmployeeNumber}
                      onChange={(event) => setNewEmployeeNumber(event.target.value)}
                    />
                    <input
                      className={styles.input}
                      placeholder="Código de empleado"
                      value={newEmployeeCode}
                      onChange={(event) => setNewEmployeeCode(event.target.value)}
                    />
                  </>
                ) : (
                  <input
                    type="email"
                    className={styles.input}
                    placeholder="empleado@empresa.com"
                    value={newEmail}
                    onChange={(event) => setNewEmail(event.target.value)}
                  />
                )}
                <button
                  type="submit"
                  className={styles.primaryButton}
                  disabled={savingList || (usesEmployeeCredentials
                    ? !newEmployeeNumber.trim() || !newEmployeeCode.trim()
                    : !newEmail.trim())}
                >
                  Agregar
                </button>
              </form>
              <div className={styles.editableList}>
                {!applicationLoaded || employeesLoading ? <p>Cargando empleados...</p> : employees.length ? employees.map((employee) => (
                  <div className={styles.editableListItem} key={employee._id}>
                    <div className={styles.editableListValue}>
                      {editingEmployeeId === employee._id ? (
                        usesEmployeeCredentials ? (
                          <>
                            <input className={styles.input} value={editingEmployeeNumber} onChange={(event) => setEditingEmployeeNumber(event.target.value)} />
                            <input className={styles.input} value={editingEmployeeCode} onChange={(event) => setEditingEmployeeCode(event.target.value)} />
                          </>
                        ) : (
                          <input type="email" className={styles.input} value={editingEmail} onChange={(event) => setEditingEmail(event.target.value)} />
                        )
                      ) : (
                        <span>{usesEmployeeCredentials
                          ? `${employee.employee_number} / ${employee.employee_code}`
                          : employee.email}</span>
                      )}
                      <small>{employee[answeredField] ? "Respondido" : "Sin responder"}</small>
                    </div>
                    <div className={styles.employeeActions}>
                      {editingEmployeeId === employee._id ? (
                        <>
                          <button type="button" disabled={savingList} onClick={() => saveEmployee(employee._id)}>Guardar</button>
                          <button type="button" onClick={() => setEditingEmployeeId(null)}>Cancelar</button>
                        </>
                      ) : (
                        <>
                          <button type="button" onClick={() => {
                            setEditingEmployeeId(employee._id);
                            setEditingEmail(employee.email || "");
                            setEditingEmployeeNumber(employee.employee_number || "");
                            setEditingEmployeeCode(employee.employee_code || "");
                          }}>Editar</button>
                          <button type="button" className={styles.dangerButton} disabled={savingList} onClick={() => deleteEmployee(employee)}>Eliminar</button>
                        </>
                      )}
                    </div>
                  </div>
                )) : <p>No hay empleados registrados.</p>}
              </div>
              <div className={styles.employeePagination}>
                <span>Página {employeesPage} de {employeesPages}</span>
                <div>
                  <button type="button" disabled={employeesPage <= 1 || employeesLoading} onClick={() => setEmployeesPage((page) => page - 1)}>Anterior</button>
                  <button type="button" disabled={employeesPage >= employeesPages || employeesLoading} onClick={() => setEmployeesPage((page) => page + 1)}>Siguiente</button>
                </div>
              </div>
            </section>
          </div>
        )}
      </section>
    </div>
  );
}
